import {
  AddButton,
  Breadcrumb,
  EnterpriseField,
  Heading,
  Page,
  RemoveButton,
  UserField,
  FormInlineError,
} from 'components';
import { Form, SelectField, Submit, useSubmitHandler } from '@ff-it/form';
import { Alert, LoadingContainer, Table, Tbody, Td, Th, Thead, Tr } from '@ff-it/ui';
import { FieldArray } from 'react-final-form-arrays';
import type { RouteObject } from 'react-router';
import type { TicketCategory } from 'modules/support/types';
import type { Option } from 'options';
import type { ReactElement } from 'react';
import useSWR from 'swr';
import { controlCol } from 'styles/style.css';

const emptyRow = {
  category: null,
  enterprise: null,
  user: null,
};

const categoryOptions: Option<TicketCategory>[] = [
  {
    value: 'ADD_COMPANY',
    label: 'Add company',
  },
  {
    value: 'ADD_OUTLET_OR_SERVICE_CATEGORY',
    label: 'Add outlet or service category',
  },
  {
    value: 'ADD_CLIENT',
    label: 'Add client',
  },
];

export function TicketAssignees(): ReactElement {
  const url = `support/assignees/`;
  const {
    isLoading,
    data: item,
    mutate,
  } = useSWR<any>({
    url,
    method: 'GET',
  });

  const onSubmit = useSubmitHandler<any, any>(
    { url, method: 'PUT' },
    { onSuccess: (v) => mutate(v, { revalidate: false }) },
  );

  return (
    <Page>
      <Breadcrumb>Ticket assignees</Breadcrumb>
      <Heading title="Ticket assignees" />
      <Alert variant="info" mb="3">
        Concrete agency has precedence over any
      </Alert>
      <LoadingContainer loading={isLoading}>
        {item ? (
          <Form initialValues={item} onSubmit={onSubmit} className="container px-0 ml-0" autoComplete="off">
            <FieldArray name="rows" subscription={{}}>
              {({ fields }) => (
                <>
                  <FormInlineError name="rows" />
                  <Table mb="0" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Category</Th>
                        <Th>Agency</Th>
                        <Th>User</Th>
                        <Th className={controlCol} />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {fields.map((name, index) => (
                        <Tr key={index}>
                          <Td>
                            <SelectField
                              name={`${name}.category`}
                              placeholder="Category"
                              options={categoryOptions}
                              simple
                              required
                              className="mb-0"
                            />
                          </Td>
                          <Td>
                            <EnterpriseField
                              name={`${name}.enterprise`}
                              placeholder="Agency"
                              isClearable
                              className="mb-0"
                            />
                          </Td>
                          <Td>
                            <UserField name={`${name}.user`} placeholder="User" className="mb-0" />
                          </Td>
                          <Td>
                            <RemoveButton onClick={() => fields.remove(index)} />
                          </Td>
                        </Tr>
                      ))}
                      <Tr>
                        <Td colSpan={3}>
                          <AddButton
                            onClick={() => {
                              fields.push(emptyRow);
                            }}
                          >
                            Add row
                          </AddButton>
                        </Td>
                        <td />
                      </Tr>
                    </Tbody>
                  </Table>
                </>
              )}
            </FieldArray>
            <Submit className="mt-3" />
          </Form>
        ) : null}
      </LoadingContainer>
    </Page>
  );
}

export const route: RouteObject = {
  path: 'ticket_assignees',
  element: <TicketAssignees />,
};
